import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  CalendarDays,
  ChevronLeft,
  CircleCheckBig,
  Clock3,
  FileText,
  Save,
  UserRound,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { showSnackbar } from "../../../../redux/slices/snackbar";
import useAuth from "../../../../hooks/useAuth";
import ChatComponent from "../components/ChatComponent";
import ClosePopup from "../components/ClosePopup";
import CustomerPreviewDrawer from "../components/CustomerPreviewDrawer";
import { PriorityBadge, StatusBadge } from "../components/Layout";
import useData from "../lib/useData";
import { formatDate } from "../lib/utils";
import { ticketService } from "../services/ticketService";

const EMPTY_VALUE = "—";

function DetailSelect({
  label,
  value,
  canEdit,
  options,
  displayValue,
  onChange,
  backgroundColor,
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(92px, 0.8fr) minmax(130px, 1.2fr)",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {canEdit ? (
        <FormControl size="small" fullWidth>
          <Select
            value={value ?? ""}
            onChange={(event) => onChange(event.target.value)}
            inputProps={{ "aria-label": label }}
            sx={{
              bgcolor: backgroundColor,
              "& .MuiSelect-select": { py: 1 },
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Typography variant="body2" fontWeight={600} textAlign="right">
          {displayValue}
        </Typography>
      )}
    </Box>
  );
}

function DateRow({ icon, label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
      >
        <Box sx={{ color: "text.secondary", display: "flex", flexShrink: 0 }}>
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        fontWeight={600}
        textAlign="right"
        sx={{ flexShrink: 0 }}
      >
        {value || EMPTY_VALUE}
      </Typography>
    </Box>
  );
}

export default function TicketDetail() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getData } = useAuth();
  const { idUid } = useParams();
  const filterState = useSelector((state) => state.issueFilters);
  const { ticket, fetchTicketDetails } = useData(false);

  const [dataTicket, setDataTicket] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isCustomerPanelOpen, setIsCustomerPanelOpen] = useState(false);
  const [customerPreview, setCustomerPreview] = useState(null);
  const [customerPreviewLoading, setCustomerPreviewLoading] = useState(false);
  const [customerPreviewError, setCustomerPreviewError] = useState("");

  const canEditSelects = dataTicket?.cOpt === 1;
  const canSendMessages = dataTicket?.cMess === 1;

  useEffect(() => {
    if (ticket) setDataTicket(ticket);
  }, [ticket]);

  useEffect(() => {
    if (idUid) fetchTicketDetails(idUid, navigate);
  }, [idUid, fetchTicketDetails, navigate]);

  const getOptions = (label) => {
    const filter = filterState?.options?.find(
      (option) => option.label?.toLowerCase() === label.toLowerCase()
    );
    const dataset = filter?.dataset?.dataset ?? filter?.dataset;
    return Array.isArray(dataset) ? dataset : [];
  };

  const getSelectedLabel = (label, value, fallback = EMPTY_VALUE) =>
    getOptions(label).find((option) => String(option.value) === String(value))
      ?.label ?? fallback;

  const priorityLabel = getSelectedLabel(
    "Priorità",
    dataTicket.idPriorita,
    ticket?.priorita
  );
  const statusLabel = getSelectedLabel(
    "Stato",
    dataTicket.idStato,
    ticket?.stato
  );

  const requesterName =
    [ticket?.customerName, ticket?.customerSurname].filter(Boolean).join(" ") ||
    ticket?.richiedente ||
    [ticket?.nome, ticket?.cognome].filter(Boolean).join(" ") ||
    "Richiedente";
  const requesterEmail =
    ticket?.customerMail || ticket?.customerEmail || ticket?.email;
  const requesterAvatar = ticket?.customerAvatar || undefined;
  const requesterInitials = requesterName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  const handleShowSnackbar = (message, severity) => {
    dispatch(showSnackbar({ message, severity }));
  };

  const handleChangeFields = (field, value) => {
    setDataTicket((previous) => ({
      ...previous,
      [field]: Number(value),
    }));
  };

  const handleClosePopup = () => {
    setOpenDialog(false);
    setNote("");
  };

  const handleOpenCustomerPanel = async () => {
    if (!ticket.customerIdUid) return;

    setIsCustomerPanelOpen(true);
    setCustomerPreviewLoading(true);
    setCustomerPreviewError("");
    setCustomerPreview(null);

    try {
      const response = await getData({
        action: "getPreviewInfo",
        type: "utente",
        value: ticket.customerIdUid,
      });

      if (response?.status === 1) {
        setCustomerPreview(response.data);
      } else {
        setCustomerPreviewError(
          response?.message || "Impossibile recuperare i dati del richiedente."
        );
      }
    } catch (error) {
      setCustomerPreviewError(
        error?.message || "Impossibile recuperare i dati del richiedente."
      );
    } finally {
      setCustomerPreviewLoading(false);
    }
  };

  const handleSaveClick = () => {
    if (dataTicket.idStato === 5) {
      setOpenDialog(true);
    } else {
      handleSaveTicket();
    }
  };

  const handleSaveTicket = async () => {
    setIsSaving(true);
    try {
      const response = await ticketService.handleUpdateTicket({
        ...dataTicket,
        closeNote: note,
      });

      if (response.status === -1) {
        sessionStorage.clear();
      } else if (response.status === 0) {
        handleShowSnackbar(response.message, "error");
      } else {
        handleShowSnackbar(response.message, "success");
        await fetchTicketDetails(idUid, navigate);
      }
      handleClosePopup();
    } catch (error) {
      handleShowSnackbar(
        error?.message || "Impossibile salvare le modifiche.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!ticket) {
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: -1 }}>
      <ClosePopup
        open={openDialog}
        handleClosePopup={handleClosePopup}
        handleSave={handleSaveTicket}
        note={note}
        setNote={setNote}
      />
      <CustomerPreviewDrawer
        open={isCustomerPanelOpen}
        onClose={() => setIsCustomerPanelOpen(false)}
        customerIdUid={ticket.customerIdUid}
        customerName={requesterName}
        customerAvatar={requesterAvatar}
        preview={customerPreview}
        loading={customerPreviewLoading}
        error={customerPreviewError}
      />

      <Box
        component="header"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1.5 },
            minWidth: 0,
            flex: 1,
          }}
        >
          <IconButton
            onClick={() => navigate("/pages/supporto")}
            aria-label="Torna all'elenco delle richieste"
          >
            <ChevronLeft size={22} />
          </IconButton>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap={{ xs: "nowrap", sm: "wrap" }}
            useFlexGap
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Typography
              variant="overline"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: "primary.main",
                px: 1.25,
                py: 0.25,
                borderRadius: 1.5,
                fontWeight: 800,
                lineHeight: 1.8,
                flexShrink: 0,
              }}
            >
              {ticket.codice}
            </Typography>
            <Typography
              variant="h5"
              component="h1"
              fontWeight={750}
              noWrap
              sx={{
                lineHeight: 1.2,
                minWidth: 0,
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: { xs: "nowrap", sm: "normal" },
                overflowWrap: { sm: "anywhere" },
              }}
            >
              {ticket.titolo}
            </Typography>
          </Stack>
        </Box>

        <IconButton
          aria-label="Salva"
          disabled={!canEditSelects || isSaving}
          onClick={handleSaveClick}
          sx={{
            display: { xs: "inline-flex", sm: "none" },
            flexShrink: 0,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
            "&.Mui-disabled": {
              bgcolor: alpha(theme.palette.text.primary, 0.08),
              color: "text.disabled",
            },
          }}
        >
          {isSaving ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <Save size={19} />
          )}
        </IconButton>

        <Button
          variant="contained"
          startIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Save size={18} />
            )
          }
          disabled={!canEditSelects || isSaving}
          onClick={handleSaveClick}
          sx={{
            display: { xs: "none", sm: "inline-flex" },
            whiteSpace: "nowrap",
          }}
        >
          {isSaving ? "Salvataggio…" : "Salva"}
        </Button>
      </Box>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
              <CardContent
                sx={{
                  p: { xs: 3, sm: 4 },
                  "&:last-child": { pb: { xs: 3, sm: 4 } },
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  gap={1.5}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" fontWeight={750}>
                    Richiesta iniziale
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {statusLabel && statusLabel !== EMPTY_VALUE ? (
                      <StatusBadge status={statusLabel} />
                    ) : null}
                    {priorityLabel && priorityLabel !== EMPTY_VALUE ? (
                      <PriorityBadge priority={priorityLabel} />
                    ) : null}
                  </Stack>
                </Stack>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    pl: { xs: 1, sm: 3.75 },
                    lineHeight: 1.75,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {ticket.descrizione || "Nessuna descrizione disponibile."}
                </Typography>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{ borderRadius: 2.5, overflow: "hidden" }}
            >
              <Box sx={{ px: { xs: 3, sm: 4 }, pt: 3, pb: 2 }}>
                <Typography variant="h5" fontWeight={750}>
                  Attività
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, pl: { xs: 1, sm: 3.75 } }}
                >
                  Cronologia dei messaggi e degli aggiornamenti della richiesta
                </Typography>
              </Box>
              <ChatComponent canSendMessages={canSendMessages} />
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
              <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ mb: 2.5 }}
                >
                  <UserRound size={19} />
                  <Typography variant="h6" fontWeight={750}>
                    Richiedente
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ pl: { xs: 1, sm: 3.75 } }}
                >
                  <Avatar
                    src={requesterAvatar}
                    alt={requesterName}
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: "primary.main",
                      fontWeight: 800,
                    }}
                  >
                    {requesterInitials || "R"}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    {ticket.customerIdUid ? (
                      <ButtonBase
                        onClick={handleOpenCustomerPanel}
                        aria-label={`Apri i dettagli di ${requesterName}`}
                        sx={{
                          borderRadius: 1,
                          textAlign: "left",
                          justifyContent: "flex-start",
                          "&:hover .customer-name": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        <Typography
                          className="customer-name"
                          fontWeight={700}
                          color="primary.main"
                        >
                          {requesterName}
                        </Typography>
                      </ButtonBase>
                    ) : (
                      <Typography fontWeight={700}>{requesterName}</Typography>
                    )}
                    {requesterEmail ? (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {requesterEmail}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
              <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ mb: 2.5 }}
                >
                  <FileText size={19} />
                  <Typography variant="h6" fontWeight={750}>
                    Dettagli
                  </Typography>
                </Stack>
                <Stack spacing={2} sx={{ pl: { xs: 1, sm: 3.75 } }}>
                  <DetailSelect
                    label="Priorità"
                    value={dataTicket.idPriorita}
                    canEdit={canEditSelects}
                    options={getOptions("Priorità")}
                    displayValue={getSelectedLabel(
                      "Priorità",
                      dataTicket.idPriorita
                    )}
                    onChange={(value) =>
                      handleChangeFields("idPriorita", value)
                    }
                    backgroundColor={alpha(
                      theme.palette.background.default,
                      0.45
                    )}
                  />
                  <DetailSelect
                    label="Stato"
                    value={dataTicket.idStato}
                    canEdit={canEditSelects}
                    options={getOptions("Stato")}
                    displayValue={getSelectedLabel("Stato", dataTicket.idStato)}
                    onChange={(value) => handleChangeFields("idStato", value)}
                    backgroundColor={alpha(
                      theme.palette.background.default,
                      0.45
                    )}
                  />
                  <DetailSelect
                    label="Categoria"
                    value={dataTicket.idCategoria}
                    canEdit={canEditSelects}
                    options={getOptions("Categoria")}
                    displayValue={getSelectedLabel(
                      "Categoria",
                      dataTicket.idCategoria
                    )}
                    onChange={(value) =>
                      handleChangeFields("idCategoria", value)
                    }
                    backgroundColor={alpha(
                      theme.palette.background.default,
                      0.45
                    )}
                  />
                  <DetailSelect
                    label="Tipo"
                    value={dataTicket.idTipo}
                    canEdit={canEditSelects}
                    options={getOptions("Tipo")}
                    displayValue={getSelectedLabel("Tipo", dataTicket.idTipo)}
                    onChange={(value) => handleChangeFields("idTipo", value)}
                    backgroundColor={alpha(
                      theme.palette.background.default,
                      0.45
                    )}
                  />
                  <Divider />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Assegnato a
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      textAlign="right"
                    >
                      {ticket.assegnatoA || ticket.assegnatario || "Vincenzo"}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
              <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ mb: 2.5 }}
                >
                  <Clock3 size={19} />
                  <Typography variant="h6" fontWeight={750}>
                    Tempistiche
                  </Typography>
                </Stack>
                <Stack spacing={2.25} sx={{ pl: { xs: 1, sm: 3.75 } }}>
                  <DateRow
                    icon={<CalendarDays size={17} />}
                    label="Data apertura"
                    value={formatDate(ticket.dataApertura)}
                  />
                  <DateRow
                    icon={<Clock3 size={17} />}
                    label="Ultimo aggiornamento"
                    value={formatDate(ticket.dataUltimoAggiornamento)}
                  />
                  {ticket.dataChiusura ? (
                    <DateRow
                      icon={<CircleCheckBig size={17} />}
                      label="Data chiusura"
                      value={formatDate(ticket.dataChiusura)}
                    />
                  ) : null}
                </Stack>
              </CardContent>
            </Card>

            {ticket.notaChiusura ? (
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2.5,
                  borderColor: alpha(theme.palette.success.main, 0.35),
                  bgcolor: alpha(theme.palette.success.main, 0.04),
                }}
              >
                <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <CircleCheckBig size={19} />
                    <Typography variant="h6" fontWeight={750}>
                      Nota di chiusura
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{
                      pl: { xs: 1, sm: 3.75 },
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.65,
                    }}
                  >
                    {ticket.notaChiusura}
                  </Typography>
                </CardContent>
              </Card>
            ) : null}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
